<template>
    <!-- WARNING this file is generated edits made will be lost on next generation -->
    <div>
        <NavBar></NavBar>
        <div class="container-fluid">
            <b-row>
            <!-- Sidebar -->
            <TOCChapter chapter-id="Chap23Errors"></TOCChapter>
				<b-col ></b-col>
                <!-- Main Content -->
                <b-col role="main" md="6" >
					<ChapterHeading chapter-title="Chapter 23: Errors" image-name="errors.jpg" image-alt="Errors"></ChapterHeading>
                    <!-- Contents BEGIN -->
                    <div id="what-will-you-learn-in-this-chapter" class="anchor"></div>
<h1 data-number="1"><span class="header-section-number">1</span> What will you learn in this chapter? <a href="#what-will-you-learn-in-this-chapter"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h1>
<ul>
<li><p>What are errors?</p></li>
<li><p>How to create an error?</p></li>
<li><p>How to handle errors?</p></li>
</ul>
<div id="technical-concepts-covered" class="anchor"></div>
<h1 data-number="2"><span class="header-section-number">2</span> Technical concepts covered <a href="#technical-concepts-covered"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h1>
<ul>
<li><p>Errors</p></li>
<li><p>The error interface</p></li>
<li><p>Sentinel errors</p></li>
<li><p>Opaque errors</p></li>
<li><p>Unwrapping errors</p></li>
</ul>
<div id="the-term-error-covers-four-different-concepts" class="anchor"></div>
<h1 data-number="3"><span class="header-section-number">3</span> The term error covers four different concepts <a href="#the-term-error-covers-four-different-concepts"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h1>
<p>The IEEE glossary <b-link class="citation" data-cites="institute1990ieee" href="#institute1990ieee" >[@institute1990ieee]</b-link> lists four different meanings for the term “error” :</p>
<ol type="1">
<li><p>The difference between a computed/observed/measured value or condition and the true/specified/theoretically value or condition.</p>
<ol type="1">
<li>Example: A program computes the distance between two objects. The program outputs 30m, but the actual distance is 28. The program has generated an error.</li>
</ol></li>
<li><p>An incorrect step or data definition into a program. Usually named a “<strong>fault</strong>”.</p>
<ol type="1">
<li>Example: A function takes as input a string and should convert it to a float. The string is malformed; the function cannot perform the conversion.</li>
</ol></li>
<li><p>An incorrect result that deviated from the expected. Usually named a “<strong>failure</strong>”.</p>
<ol type="1">
<li><p>Example: A function has been designed to compute the total price for a specific booking. When the function is run, the price is 145.99$, but when the client computed it with a calculator, the result is 189$. The function result is erroneous.</p></li>
<li><p>This is close to the first definition</p></li>
</ol></li>
<li><p>A human action that produces an incorrect result. Usually named a “<strong>mistake</strong>”.</p>
<ol type="1">
<li>For example, a database admin is asked to insert a row in a production database. He eventually deletes the entire database. (ouch:)</li>
</ol></li>
</ol>
<p>As programmers, we will need to prepare our programs against potential <strong>faults</strong> and <strong>failures.</strong> We can also avoid <strong>mistakes</strong> by providing extended documentation of our programs.</p>
<div id="example-a-program-that-can-fail" class="anchor"></div>
<BuyCopyInvite></BuyCopyInvite>
<h1 data-number="4"><span class="header-section-number">4</span> Example: a program that can fail <a href="#example-a-program-that-can-fail"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h1>
<div id="context-storing-configuration" class="anchor"></div>
<h2 data-number="4.1"><span class="header-section-number">4.1</span> Context : storing configuration <a href="#context-storing-configuration"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<p>Our Hotel management software needs to have access to some configuration values: the name of the hotel, the VAT rate for the country, the username and password of its database system ...etc.</p>
<p>One easy solution would be to write those configuration values directly into the program. It’s not impossible but not recommended; we cannot store a password into our program’s compiled binary, neither a VAT rate. We would need to recompile it if the password change or the vat rate change... In addition to that, it’s a security issue; the password will be written directly in the binary, making it readable.</p>
<p>In this example, we will take this solution to load the file’s configuration. Let’s see how to implement it.</p>
<div id="implementation" class="anchor"></div>
<h2 data-number="4.2"><span class="header-section-number">4.2</span> Implementation <a href="#implementation"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<p>The first file is, as usual, a go.mod (initialized with go mod init) :</p>
<pre v-highlightjs><code class="go" v-pre >module &quot;maximilien-andile.com/errors/error-example&quot;

go 1.16</code></pre>
<p>Then we have the package config :</p>
<pre v-highlightjs><code class="go" v-pre >// errors/example-failure/config/main.go
package config

import (
    &quot;fmt&quot;
    &quot;io/ioutil&quot;
)

func load() []byte {
    dat, err := ioutil.ReadFile(&quot;/tmp/myHotelApp/config.txt&quot;)
    fmt.Println(err)
    return dat
}

func Print() {
    fmt.Println(string(load()))
}</code></pre>
<p>We have an unexported function in this package: <span v-highlightjs><code class="go" v-pre style="display: inline">load</code></span>. It will read a file. It uses the function <span v-highlightjs><code class="go" v-pre style="display: inline">ReadFile</code></span> from the package <span v-highlightjs><code class="go" v-pre style="display: inline">ioutil</code></span>. The file that we will load is identified by its path : <span v-highlightjs><code class="go" v-pre style="display: inline">"/tmp/myHotelApp/config.txt"</code></span></p>
<p>This function is called by the exported function <span v-highlightjs><code class="go" v-pre style="display: inline">Print</code></span>. Which will print the data loaded from the file.</p>
<p>The main file will call <span v-highlightjs><code class="go" v-pre style="display: inline">config.Print</code></span>:</p>
<pre v-highlightjs><code class="go" v-pre >// errors/example-failure/main.go
package main

import &quot;maximilien-andile.com/errors/error-example/config&quot;

func main() {
    config.Print()
}</code></pre>
<div id="build-and-run" class="anchor"></div>
<h2 data-number="4.3"><span class="header-section-number">4.3</span> Build and run <a href="#build-and-run"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<pre v-highlightjs><code class="go" v-pre >$ go build main.go
./main
open /tmp/myHotelApp/config.txt: no such file or directory</code></pre>
<p>Go is telling us that it cannot open the file because it does not exist. The displayed text is not in our program; it is contained in the variable err. The call to ioutil.ReadFile returns two results :</p>
<pre v-highlightjs><code class="go" v-pre >dat, err := ioutil.ReadFile(&quot;/tmp/myHotelApp/config.txt&quot;)
fmt.Println(err)</code></pre>
<p>Let’s check the signature of ReadFile! To do that, we have two options :</p>
<ul>
<li><p>If you use an IDE, some can go to the function definition with a click.</p>
<ul>
<li>Here is the function signature :</li>
</ul>
<!-- -->
<pre v-highlightjs><code class="go" v-pre >func ReadFile(filename string) ([]byte, error)</code></pre></li>
<li><p>If you do not use one, you can still go to the online documentation</p>
<ul>
<li>Go to <strong>pkg.go.dev</strong> and type <strong>ioutil</strong></li>
</ul></li>
</ul>
<figure>
<b-img :src="require('@/assets/images/go_package_discovery.png')" alt="pkg.go.dev is a website to view the documentation of go package[fig:pkg.go.dev-ioutil]"  fluid thumbnail class="img-book"></b-img><figcaption aria-hidden="true">pkg.go.dev is a website to view the documentation of go package<span id="fig:pkg.go.dev-ioutil" label="fig:pkg.go.dev-ioutil">[fig:pkg.go.dev-ioutil]</span></figcaption>
</figure>
<p>We note that the function returns two results :</p>
<ul>
<li><p>A slice of bytes</p></li>
<li><p>An error</p></li>
</ul>
<p>The function documentation detail how the function works :</p>
<blockquote>
<p>“ReadFile reads the file named by filename and returns the contents. A successful call returns err == nil, not err == EOF. Because ReadFile reads the whole file, it does not treat an EOF from Read as an error to be reported.”</p>
</blockquote>
<ul>
<li><p>The function will attempt to read the whole file.</p></li>
<li><p>We learn that in the case of success, the value of error is nil</p></li>
<li><p>When there is an error, the second result’s value is not nil.</p></li>
</ul>
<div id="fix-the-error" class="anchor"></div>
<h2 data-number="4.4"><span class="header-section-number">4.4</span> Fix the error <a href="#fix-the-error"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<p>Let’s create a file in the requested location to test our program’s behavior. To do that, we will create a file with the command line :</p>
<pre v-highlightjs><code class="go" v-pre >$ mkdir /tmp/myHotelApp
$ echo &quot;hello&quot; &gt; /tmp/myHotelApp/config.txt</code></pre>
<p>The first command will create the directory <span v-highlightjs><code class="go" v-pre style="display: inline">/tmp/myHotelApp</code></span>. The second command will write <span v-highlightjs><code class="go" v-pre style="display: inline">hello</code></span> to the file <span v-highlightjs><code class="go" v-pre style="display: inline">/tmp/myHotelApp/config.tx</code></span> <strong>.</strong>Note that we will delete the contents already in the file. (to append content use <span v-highlightjs><code class="go" v-pre style="display: inline">&gt;&gt;</code></span> instead of <span v-highlightjs><code class="go" v-pre style="display: inline">&gt;</code></span>).</p>
<p>Let’s run a second time the program :</p>
<pre v-highlightjs><code class="go" v-pre >$ ./main
&lt;nil&gt;
hello</code></pre>
<p>This time the value of <span v-highlightjs><code class="go" v-pre style="display: inline">err</code></span> printed is <span v-highlightjs><code class="go" v-pre style="display: inline">&lt;nil&gt;</code></span>. The program reads the file successfully! The content is printed on the screen.</p>
<div id="error-interface-as-result" class="anchor"></div>
<h1 data-number="5"><span class="header-section-number">5</span> Error interface as result <a href="#error-interface-as-result"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h1>
<p>Generally, Go functions and methods return two results :</p>
<ul>
<li><p>The result(s) of the function, what we expect the function to produce.</p></li>
<li><p>An element of type <span v-highlightjs><code class="go" v-pre style="display: inline">error</code></span>.</p>
<ul>
<li><p>Ex: ioutil.ReadFile’s first result is a slice of bytes containing the file’s content. The second result is an error</p>
<pre v-highlightjs><code class="go" v-pre >func ReadFile(filename string) ([]byte, error)</code></pre></li>
<li><p>Ex : <span v-highlightjs><code class="go" v-pre style="display: inline">fmt.Println</code></span> returns the number of bytes written and an error</p>
<pre v-highlightjs><code class="go" v-pre >func Println(a ...interface{}) (n int, err error)</code></pre></li>
</ul></li>
</ul>
<p>When the error is equal to <span v-highlightjs><code class="go" v-pre style="display: inline">nil</code></span> then the function execution is considered to be successful. When the error is not nil, then <strong>something went wrong</strong> and we can discard the result.</p>
<p>Some function/methods return just an element of type interface error :</p>
<pre v-highlightjs><code class="go" v-pre >csvWriter := csv.NewWriter(csvFile)
err = csvWriter.Write(invoiceData)</code></pre>
<div id="the-error-interface" class="anchor"></div>
<h4 data-number="5.0.0.1"><span class="header-section-number">5.0.0.1</span> The error interface <a href="#the-error-interface"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h4>
<p>Here is the error interface defined in the standard library</p>
<pre v-highlightjs><code class="go" v-pre >type error interface {
    Error() string
}</code></pre>
<p>All errors returned in the standard library will return this interface. When an error is printed <span v-highlightjs><code class="go" v-pre style="display: inline">Error</code></span> is executed.</p>
<div id="modification-of-the-load-function-signature" class="anchor"></div>
<h2 data-number="5.1"><span class="header-section-number">5.1</span> Modification of the Load function signature <a href="#modification-of-the-load-function-signature"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<p>Here is a new version of the function <span v-highlightjs><code class="go" v-pre style="display: inline">load</code></span> :</p>
<pre v-highlightjs><code class="go" v-pre >// errors/example-fix/config/main.go
package config

import (
    &quot;io/ioutil&quot;
)

func Load() (string, error) {
    data, err := ioutil.ReadFile(&quot;/tmp/myHotelApp/config.txt&quot;)
    if err != nil {
        return &quot;&quot;, err
    }
    return string(data), nil
}</code></pre>
<p>What’s new?</p>
<ul>
<li><p>The function is now <strong>exported</strong>.</p></li>
<li><p>The result parameters have changed. We now return a <span v-highlightjs><code class="go" v-pre style="display: inline">string</code></span> and an <span v-highlightjs><code class="go" v-pre style="display: inline">error</code></span>.</p></li>
<li><p>When we call <span v-highlightjs><code class="go" v-pre style="display: inline">ioutil.ReadFile</code></span> we initialize two local variables : <span v-highlightjs><code class="go" v-pre style="display: inline">data</code></span> and <span v-highlightjs><code class="go" v-pre style="display: inline">err</code></span> .</p></li>
<li><p>We then test the value or <span v-highlightjs><code class="go" v-pre style="display: inline">err</code></span> against nil. Why ?</p>
<ul>
<li><p>Because the documentation of <span v-highlightjs><code class="go" v-pre style="display: inline">ioutil.ReadFile</code></span> tell us that “A successful call returns err == nil”.</p></li>
<li><p>As a consequence, when <span v-highlightjs><code class="go" v-pre style="display: inline">err</code></span> is not equal to nil; something went wrong.</p></li>
<li><p>We do not try to understand what went wrong. We just return two results: an empty string and the error.</p></li>
<li><p>The caller will need to <strong>handle the error</strong>.</p></li>
</ul></li>
<li><p>When there is no error, we return the data retrieved in the file and the value <span v-highlightjs><code class="go" v-pre style="display: inline">nil</code></span>.</p>
<ul>
<li><p>Why do we need to return <span v-highlightjs><code class="go" v-pre style="display: inline">nil</code></span> (as second result) ?</p></li>
<li><p>Because the caller needs to know if an error happened. We signal it by valorizing the second result parameter to <span v-highlightjs><code class="go" v-pre style="display: inline">nil</code></span>.</p></li>
</ul></li>
</ul>
<div id="the-function-caller-is-notified-when-an-error-occurred" class="anchor"></div>
<h2 data-number="5.2"><span class="header-section-number">5.2</span> The function caller is notified when an error occurred <a href="#the-function-caller-is-notified-when-an-error-occurred"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<pre v-highlightjs><code class="go" v-pre >// errors/example-fix/main.go
package main

import (
    &quot;fmt&quot;
    &quot;log&quot;

    &quot;maximilien-andile.com/errors/errorExampleFixed/config&quot;
)

func main() {
    confData, err := config.Load()
    if err != nil {
        log.Fatalf(&quot;Impossible to load application config because: %s&quot;, err)
    }
    fmt.Println(confData)
}</code></pre>
<div id="code-description" class="anchor"></div>
<h3 data-number="5.2.1"><span class="header-section-number">5.2.1</span> Code description <a href="#code-description"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h3>
<div id="program-test" class="anchor"></div>
<h3 data-number="5.2.2"><span class="header-section-number">5.2.2</span> Program test <a href="#program-test"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h3>
<p>Let’s test this new version :</p>
<pre v-highlightjs><code class="go" v-pre >$ go build main.go
$./main
hello</code></pre>
<p>Let’s try to make it crash! To do that, we will delete the config file :</p>
<pre v-highlightjs><code class="go" v-pre >$ rm /tmp/myHotelApp/config.txt
$ ./main
2020/03/07 18:13:21 Impossible to load application config because open /tmp/myHotelApp/config.txt: no such file or directory</code></pre>
<p>Let’s check the exit code returned by our program :</p>
<pre v-highlightjs><code class="go" v-pre >$ echo $?
1</code></pre>
<p>The exit code is 1.</p>
<div id="about-exit-code" class="anchor"></div>
<h4 data-number="5.2.2.1"><span class="header-section-number">5.2.2.1</span> About exit code <a href="#about-exit-code"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h4>
<p>In UNIX and Windows-based systems, programs return an <strong>exit code</strong> when they stop. This code is here to warn the person (or the machine) that launched the program if something went wrong or not. A program that returns a 0 as exit code is considered to have executed its task without any issues by convention. When a program returns another code other than 0, something went wrong.</p>
<p>Some programs will return a specific exit code in case of specific errors. In their documentation, you will find a mapping between the code and their signification.</p>
<p>In Go, we can force the os to exit with the following call :</p>
<pre v-highlightjs><code class="go" v-pre >os.Exit(1)</code></pre>
<p>You can change the value one by another integer from 0 to 125 to make your program portable to all systems.</p>
<pre v-highlightjs><code class="go" v-pre >os.Exit(120)</code></pre>
<div id="how-to-create-standalone-errors" class="anchor"></div>
<h1 data-number="6"><span class="header-section-number">6</span> How to create standalone errors? <a href="#how-to-create-standalone-errors"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h1>
<p>You can use several met to create errors :</p>
<div id="errors.new" class="anchor"></div>
<h2 data-number="6.1"><span class="header-section-number">6.1</span> errors.New <a href="#errors.new"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<p>The <span v-highlightjs><code class="go" v-pre style="display: inline">errors</code></span> package from the standard library propose an implementation of the error interface. It allows you to create an error in your application easily. Let’s take an example usage :</p>
<pre v-highlightjs><code class="go" v-pre >// errors/example-fix-2/config/config.go
package config

import (
    &quot;errors&quot;
    &quot;io/ioutil&quot;
)

const fileHeader = &quot;APPCONF&quot;

func Load() (string, error) {
    data, err := ioutil.ReadFile(&quot;/tmp/myHotelApp/config.txt&quot;)
    if err != nil {
        return &quot;&quot;, err
    }
    conf := string(data)
    if conf[0:8] != fileHeader {
        return &quot;&quot;, errors.New(&quot;the config file do not begin by accepted header&quot;)
    }
    return conf, nil
}</code></pre>
<p>Here we add another test to our config loader. The file must begin with specific characters. We take the first seven characters (by slicing the conf variable: we take the character from index 0 included to index eight excluded).</p>
<p>When the file does not begin by the value of the const <span v-highlightjs><code class="go" v-pre style="display: inline">fileHeader</code></span>. We return an empty string along with a new error built with the function <span v-highlightjs><code class="go" v-pre style="display: inline">New</code></span> from the errors package. Let’s test our error</p>
<pre v-highlightjs><code class="go" v-pre >$ go run main.go
2020/03/06 19:14:07 Impossible to load application config because the config file does not begin by accepted header
exit status 1</code></pre>
<div id="fmt.errorf" class="anchor"></div>
<h2 data-number="6.2"><span class="header-section-number">6.2</span> fmt.Errorf <a href="#fmt.errorf"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<p>The package fmt allows you to create errors with <span v-highlightjs><code class="go" v-pre style="display: inline">fmt.Errorf</code></span>. Here is an example :</p>
<pre v-highlightjs><code class="go" v-pre >func Load() (string, error) {
    //...
    if conf[0:8] != fileHeader {
        return &quot;&quot;, fmt.Errorf(&quot;the config file do not begin by accepted header&quot;)
    }
    //..
}</code></pre>
<p>Internally fmt.Errorf will call <span v-highlightjs><code class="go" v-pre style="display: inline">errors.New</code></span>.!</p>
<div id="sentinel-error" class="anchor"></div>
<h2 data-number="6.3"><span class="header-section-number">6.3</span> Sentinel error <a href="#sentinel-error"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<p>Sentinel errors are common in the standard library. They are exported variables of type error. Functions and methods of the package will return them. Callers will be able to take actions if such an error is returned (see next section to see how to handle sentinel errors)</p>
<pre v-highlightjs><code class="go" v-pre >// errors/sentinel/config/config.go
package config

import (
    &quot;errors&quot;
    &quot;io/ioutil&quot;
)

var ErrNoConfigFile = errors.New(&quot;no config file at the specified location: /tmp/myHotelApp/config.txt&quot;)

func Load() (string, error) {
    data, err := ioutil.ReadFile(&quot;/tmp/myHotelApp/config.txt&quot;)
    if err != nil {
        return &quot;&quot;, ErrNoConfigFile
    }
    return string(data), nil
}</code></pre>
<p>Here is an example in the package <span v-highlightjs><code class="go" v-pre style="display: inline">rsa</code></span>:</p>
<pre v-highlightjs><code class="go" v-pre >var ErrMessageTooLong = errors.New(&quot;crypto/rsa: message too long for RSA public key size&quot;)</code></pre>
<p>And in the package <span v-highlightjs><code class="go" v-pre style="display: inline">io</code></span>:</p>
<pre v-highlightjs><code class="go" v-pre >var ErrShortWrite = errors.New(&quot;short write&quot;)
var ErrShortBuffer = errors.New(&quot;short buffer&quot;)
var EOF = errors.New(&quot;EOF&quot;)
var ErrNoProgress = errors.New(&quot;multiple Read calls return no data or error&quot;)</code></pre>
<div id="a-custom-type-that-implements-the-error-interface" class="anchor"></div>
<h2 data-number="6.4"><span class="header-section-number">6.4</span> A custom type that implements the error interface <a href="#a-custom-type-that-implements-the-error-interface"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<pre v-highlightjs><code class="go" v-pre >type HeaderError struct {
    FaultyHeader string
}

func (e *HeaderError) Error() string {
    return fmt.Sprintf(&quot;Bad header. Provided %s, expected : APPCONF&quot;, e.FaultyHeader)
}</code></pre>
<p>The type <span v-highlightjs><code class="go" v-pre style="display: inline">HeaderError</code></span> implements the <span v-highlightjs><code class="go" v-pre style="display: inline">error</code></span> interface. We can use it in our function :</p>
<pre v-highlightjs><code class="go" v-pre >func Load() (string, error) {
    //...
    if conf[0:8] != fileHeader {
        return &quot;&quot;, &amp;HeaderError{FaultyHeader:conf[0:8]}
    }
    //..
}</code></pre>
<div id="how-to-wrap-an-error-into-another-error" class="anchor"></div>
<h1 data-number="7"><span class="header-section-number">7</span> How to wrap an error into another error ? <a href="#how-to-wrap-an-error-into-another-error"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h1>
<div id="what-is-wrapping" class="anchor"></div>
<h2 data-number="7.1"><span class="header-section-number">7.1</span> What is wrapping? <a href="#what-is-wrapping"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<p>Function or methods may encounter an error due to another function call. Here is an example :</p>
<pre v-highlightjs><code class="go" v-pre >// errors/wrapping/useCase/main.go 


func transferFileContents(filename string) error {
    contents, err := ioutil.ReadFile(filename)
    if err != nil {
        return err
    }
    err = ioutil.WriteFile(&quot;/tmp/filecontents&quot;, contents, 0644)
    if err != nil {
        return err
    }
    return nil
}</code></pre>
<p><span v-highlightjs><code class="go" v-pre style="display: inline">transferFileContents</code></span> makes two function calls: <span v-highlightjs><code class="go" v-pre style="display: inline">ioutil.ReadFile</code></span> and <span v-highlightjs><code class="go" v-pre style="display: inline">ioutil.WriteFile</code></span>. Those two function return errors. When such an error occurs, the function return directly the errors returned by the ioutil package functions. When an error occurs, the caller will receive the error without additional context :</p>
<pre v-highlightjs><code class="go" v-pre >open /tmp/myHotelApp/config.txt: no such file or directory</code></pre>
<p>What we could do is wrap this error into a new error to add context to it :</p>
<pre v-highlightjs><code class="go" v-pre >during the transfer of file context, something went wrong: open /tmp/myHotelApp/config.txt: no such file or directory</code></pre>
<div id="with-fmt.errorf" class="anchor"></div>
<h2 data-number="7.2"><span class="header-section-number">7.2</span> with fmt.Errorf <a href="#with-fmt.errorf"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<p>To wrap the error, you can use the following syntax :</p>
<pre v-highlightjs><code class="go" v-pre >// errors/wrapping/fmt/main.go
package main

// ...

func transferFileContents(filename string) error {
    contents, err := ioutil.ReadFile(filename)
    if err != nil {
        return fmt.Errorf(&quot;during file transfer impossible to open source file: %w&quot;, err)
    }
    err = ioutil.WriteFile(&quot;/tmp/filecontents&quot;, contents, 0644)
    if err != nil {
        return fmt.Errorf(&quot;during file transfer impossible to write source file: %w&quot;, err)
    }
    return nil
}</code></pre>
<p>To wrap the error into the new error, we use the verb <span v-highlightjs><code class="go" v-pre style="display: inline">%w</code></span><strong>.</strong></p>
<p>Let’s create a simple application that uses this function :</p>
<pre v-highlightjs><code class="go" v-pre >// errors/wrapping/fmt/main.go
package main

import (
    &quot;fmt&quot;
    &quot;io/ioutil&quot;
    &quot;log&quot;
)

func main() {
    err := transferFileContents(&quot;/my/imaginary/file&quot;)
    if err != nil {
        log.Printf(&quot;error occured: %s&quot;, err)
    }
}</code></pre>
<p>When an error is returned, the logged message is now :</p>
<pre v-highlightjs><code class="go" v-pre >2020/03/22 19:09:13 error occurred: during file transfer impossible to open source file: open /my/imaginary/file: no such file or directory</code></pre>
<p>Instead of :</p>
<pre v-highlightjs><code class="go" v-pre >2020/03/22 19:10:18 error occured: open /my/imaginary/file: no such file or directory</code></pre>
<div id="with-a-custom-error-type-and-the-unwrap-method" class="anchor"></div>
<h2 data-number="7.3"><span class="header-section-number">7.3</span> With a custom error type and the Unwrap method <a href="#with-a-custom-error-type-and-the-unwrap-method"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<p>When you define a custom error type, you can implement the <span v-highlightjs><code class="go" v-pre style="display: inline">Unwrap</code></span> method :</p>
<pre v-highlightjs><code class="go" v-pre >type ReadingError struct {
    IOError  error
    Filename string
}

func (e *ReadingError) Error() string {
    return fmt.Sprintf(&quot;an error occured while attempting to read the file %s&quot;, e.Filename)
}

func (e *ReadingError) Unwrap() error {
    return e.IOError
}</code></pre>
<ul>
<li>The <span v-highlightjs><code class="go" v-pre style="display: inline">Unwrap</code></span> method returns the underlying error</li>
</ul>
<div id="how-to-handle-errors" class="anchor"></div>
<BuyCopyInvite></BuyCopyInvite>
<h1 data-number="8"><span class="header-section-number">8</span> How to handle errors? <a href="#how-to-handle-errors"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h1>
<div id="do-not-ignore-them" class="anchor"></div>
<h2 data-number="8.1"><span class="header-section-number">8.1</span> Do not ignore them! <a href="#do-not-ignore-them"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<p>You have the power to ignore errors. You should not use this power! If something went wrong, you should modify the program’s flow.</p>
<pre v-highlightjs><code class="go" v-pre >// DON&#39;T DO THAT
func main() {
    transferFileContents(&quot;/my/imaginary/file&quot;)
    log.Println(&quot;tranfer done&quot;)
}

func transferFileContents(filename string) error {
    //...
}</code></pre>
<p><span v-highlightjs><code class="go" v-pre style="display: inline">transferFileContents</code></span> returns one error. In the <span v-highlightjs><code class="go" v-pre style="display: inline">main</code></span> function we simply ignore the error. When the transfer is a failure, we are still logging that the transfer was done!</p>
<div id="option-1-treat-them-as-opaque" class="anchor"></div>
<h2 data-number="8.2"><span class="header-section-number">8.2</span> Option 1: treat them as opaque <a href="#option-1-treat-them-as-opaque"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<p>In some Go programs, errors are treated as an opaque signal. The caller will not look into errors to get their cause. They will just test if <span v-highlightjs><code class="go" v-pre style="display: inline">err</code></span> is not <span v-highlightjs><code class="go" v-pre style="display: inline">nil</code></span> :</p>
<pre v-highlightjs><code class="go" v-pre >func main() {
    err := transferFileContents(&quot;/my/imaginary/file&quot;)
    if err != nil {
        log.Fatalf(&quot;transfer impossible caused by: %s&quot;, err)
    }
    log.Println(&quot;tranfer done&quot;)
}</code></pre>
<p>The user will be warned of the error.</p>
<div id="option-2-adapt-the-program-flow-depending-on-the-error" class="anchor"></div>
<h2 data-number="8.3"><span class="header-section-number">8.3</span> Option 2: adapt the program flow depending on the error <a href="#option-2-adapt-the-program-flow-depending-on-the-error"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<div id="detect-a-sentinel-error-with-a-simple-comparison" class="anchor"></div>
<h3 data-number="8.3.1"><span class="header-section-number">8.3.1</span> Detect a sentinel error with a simple comparison <a href="#detect-a-sentinel-error-with-a-simple-comparison"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h3>
<p>The caller can detect a sentinel error returned by a function. Here is a very common example: reading a CSV file. Here is the CSV file that we will read :</p>
<pre v-highlightjs><code class="go" v-pre >John,Doe,256
Diffie,Lock,257</code></pre>
<p>Each line represents a record. The values of a record are separated by commas (CSV means Comma Separated Values). Here is the code :</p>
<pre v-highlightjs><code class="go" v-pre >// errors/handling/detect/sentinelIs/main.go
package main

import (
    &quot;encoding/csv&quot;
    &quot;errors&quot;
    &quot;fmt&quot;
    &quot;io&quot;
    &quot;log&quot;
    &quot;os&quot;
)

func main() {
    file, err := os.Open(&quot;test.csv&quot;)
    defer file.Close()
    if err != nil {
        log.Printf(&quot;impossible to open file %s&quot;, err)
        return
    }

    r := csv.NewReader(file)
    for {
        record, err := r.Read()
        if errors.Is(err, io.EOF) {
            break
        }
        if err != nil {
            log.Fatal(err)
        }
        fmt.Println(record)
    }
}</code></pre>
<p>This script will read a CSV file and iterate through it. It uses the CSV Reader. The Reader has a method <span v-highlightjs><code class="go" v-pre style="display: inline">Read</code></span> that will read one row. To read all the data in the file, we added a for loop.</p>
<p>Here is the documentation of the Read function :</p>
<figure>
<b-img :src="require('@/assets/images/csv_read_documentation.png')" alt="Documentation of Read function"  fluid thumbnail class="img-book"></b-img><figcaption aria-hidden="true">Documentation of Read function</figcaption>
</figure>
<p>When the end of the file is reached, Read will return <span v-highlightjs><code class="go" v-pre style="display: inline">nil</code></span> and the sentinel error <span v-highlightjs><code class="go" v-pre style="display: inline">io.EOF</code></span> . When <span v-highlightjs><code class="go" v-pre style="display: inline">nil</code></span> is equal to <span v-highlightjs><code class="go" v-pre style="display: inline">io.EOF</code></span> we stop the loop with <span v-highlightjs><code class="go" v-pre style="display: inline">break</code></span> . That’s the reason why we check the value of err two times :</p>
<pre v-highlightjs><code class="go" v-pre >if err == io.EOF {
    break
}
if err != nil {
    log.Printf(err)
    return
}</code></pre>
<p>In the first case, no more lines have to be read from the file; in the second case, something bad happens. In this case, we want our program to stop immediately, whereas in the second case, we just exit the loop!</p>
<div id="detect-a-sentinel-error-with-errors.is" class="anchor"></div>
<h3 data-number="8.3.2"><span class="header-section-number">8.3.2</span> Detect a sentinel error with errors.Is <a href="#detect-a-sentinel-error-with-errors.is"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h3>
<p>With the function Is from the errors package, you can detect if a specific error has occurred. Is will detect errors that have been wrapped! Here is the signature of the function :</p>
<pre v-highlightjs><code class="go" v-pre >func Is(err, target error) bool</code></pre>
<p>Two input parameters : <span v-highlightjs><code class="go" v-pre style="display: inline">err</code></span> and <span v-highlightjs><code class="go" v-pre style="display: inline">target</code></span> of type interface <span v-highlightjs><code class="go" v-pre style="display: inline">error</code></span>. The <span v-highlightjs><code class="go" v-pre style="display: inline">target</code></span> parameter is the error that you want to discover in the chain, <span v-highlightjs><code class="go" v-pre style="display: inline">err</code></span> is your actual error. Here is an example usage :</p>
<pre v-highlightjs><code class="go" v-pre >r := csv.NewReader(file)
for {
    record, err := r.Read()
    // use Is instead of an equality comparison
    if errors.Is(err, io.EOF) {
        break
    }
    if err != nil {
        log.Fatal(err)
    }
    fmt.Println(record)
}</code></pre>
<p>In this snippet, we could have replaced <span v-highlightjs><code class="go" v-pre style="display: inline">errors.Is(err, io.EOF)</code></span> by the equality comparison because <span v-highlightjs><code class="go" v-pre style="display: inline">io.EOF</code></span> is directly returned by <span v-highlightjs><code class="go" v-pre style="display: inline">r.Read()</code></span>. The next example will demonstrate that the function <span v-highlightjs><code class="go" v-pre style="display: inline">Is</code></span> is capable of detecting sentinel errors in the error chain :</p>
<pre v-highlightjs><code class="go" v-pre >// errors/handling/detect/sentinelIsChain/main.go
package main

import (
    &quot;errors&quot;
    &quot;fmt&quot;
)

func main() {
    err := foo()
    if errors.Is(err, errSentinel) {
        fmt.Println(&quot;errSentinel detected in the error chain with errors.Is&quot;)
    }
    if err == errSentinel {
        fmt.Println(&quot;errSentinel detected in the error chain by ==&quot;)
    }
}

var errSentinel = errors.New(&quot;test&quot;)

func foo() error {
    return fmt.Errorf(&quot;error : %w&quot;, bar())
}

func bar() error {
    return errSentinel
}</code></pre>
<p>Here is the execution result of this script :</p>
<pre v-highlightjs><code class="go" v-pre >errSentinel detected in the error chain with errors.Is</code></pre>
<p>In the <span v-highlightjs><code class="go" v-pre style="display: inline">main</code></span> function we call the function <span v-highlightjs><code class="go" v-pre style="display: inline">foo</code></span> which will return an error that wraps the result of <span v-highlightjs><code class="go" v-pre style="display: inline">bar</code></span>. The function <span v-highlightjs><code class="go" v-pre style="display: inline">bar</code></span> returns the sentinel error <span v-highlightjs><code class="go" v-pre style="display: inline">errSentinel</code></span>.</p>
<div id="detect-an-error-type-with-errors.as" class="anchor"></div>
<h3 data-number="8.3.3"><span class="header-section-number">8.3.3</span> Detect an error type with errors.As <a href="#detect-an-error-type-with-errors.as"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h3>
<p>The function <span v-highlightjs><code class="go" v-pre style="display: inline">errors.As</code></span> can be used to detect if an error of type X is returned in the error chain. Let’s see the signature of this function :</p>
<pre v-highlightjs><code class="go" v-pre >func As(err error, target interface{}) bool</code></pre>
<p>The function takes two arguments :</p>
<ul>
<li>err which is of type interface <span v-highlightjs><code class="go" v-pre style="display: inline">error</code></span></li>
</ul>
<p><span v-highlightjs><code class="go" v-pre style="display: inline">target</code></span> which is of type empty interface. Note that this parameter</p>
<ul>
<li><p>(A) cannot be nil</p>
<ul>
<li><p>(B) must be a pointer</p></li>
<li><p>(C) must be an interface or implement the error interface</p></li>
</ul></li>
</ul>
<p>Here is an example :</p>
<pre v-highlightjs><code class="go" v-pre >// errors/handling/detect/type/main.go
package main

import (
    &quot;errors&quot;
    &quot;fmt&quot;
    &quot;io/ioutil&quot;
    &quot;log&quot;
)

func main() {
    err := transferFileContents(&quot;/my/imaginary/file&quot;)
    var readingError *ReadingError
    if errors.As(err, &amp;readingError) {
        log.Fatalf(&quot;error of reading occured: %s: %s&quot;, readingError, readingError.Unwrap())
    }
    var writingError *WritingError
    if errors.As(err, &amp;writingError) {
        log.Fatalf(&quot;error of writing occured: %s&quot;, err)
    }
    log.Println(&quot;transfer done&quot;)
}</code></pre>
<p><span v-highlightjs><code class="go" v-pre style="display: inline">readingError</code></span> is a variable of type <span v-highlightjs><code class="go" v-pre style="display: inline">*ReadingError</code></span>.<span v-highlightjs><code class="go" v-pre style="display: inline">*ReadingError</code></span> is a pointer type. It represents all pointers to variable of type <span v-highlightjs><code class="go" v-pre style="display: inline">ReadingError</code></span>. We call <span v-highlightjs><code class="go" v-pre style="display: inline">errors.As</code></span> with two arguments : <span v-highlightjs><code class="go" v-pre style="display: inline">err</code></span> and <span v-highlightjs><code class="go" v-pre style="display: inline">&amp;readingError</code></span>. The operator <span v-highlightjs><code class="go" v-pre style="display: inline">&amp;</code></span> signal that we take the address of the variable <span v-highlightjs><code class="go" v-pre style="display: inline">readingError</code></span>. What if we give directly to <span v-highlightjs><code class="go" v-pre style="display: inline">errors.As</code></span> the variable <span v-highlightjs><code class="go" v-pre style="display: inline">readingError</code></span> ? Why not? Let’s detail why :</p>
<ul>
<li><p>What is the value of <span v-highlightjs><code class="go" v-pre style="display: inline">readingError</code></span> right after its declaration?</p>
<ul>
<li>It’s nil. It violates the rule (A).</li>
</ul></li>
<li><p>What is the type of <span v-highlightjs><code class="go" v-pre style="display: inline">readingError</code></span> ?</p>
<ul>
<li><span v-highlightjs><code class="go" v-pre style="display: inline">*ReadingError</code></span></li>
</ul></li>
<li><p>What is <span v-highlightjs><code class="go" v-pre style="display: inline">&amp;readingError</code></span> ?</p>
<ul>
<li><p>It is a pointer to the value <span v-highlightjs><code class="go" v-pre style="display: inline">readingError</code></span> which is nil.</p></li>
<li><p>It’s NOT a nil pointer. It points to a value that is nil, but the pointer himself is not nil</p></li>
<li><p>Therefore <span v-highlightjs><code class="go" v-pre style="display: inline">&amp;readingError</code></span> is not nil (A) and is a pointer (B).</p></li>
</ul></li>
<li><p>Is <span v-highlightjs><code class="go" v-pre style="display: inline">&amp;readingError</code></span> an interface?</p>
<ul>
<li>No</li>
</ul></li>
<li><p>Does <span v-highlightjs><code class="go" v-pre style="display: inline">&amp;readingError</code></span> implements the error interface?</p>
<ul>
<li>Yes! Rule C is verified.</li>
</ul></li>
</ul>
<div id="bits-of-advice" class="anchor"></div>
<h1 data-number="9"><span class="header-section-number">9</span> Bits of Advice <a href="#bits-of-advice"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h1>
<p>Errors and problems are part of programming. Your program has to handle all errors that might happen. You must think about the worst. Ask yourself what could go wrong in that line? What techniques could employ an evil user to make your program fail?</p>
<table>
<caption>Error Handling recommendations</caption>
<thead>
<tr class="header">
<th style="text-align: center;">Advice</th>
</tr>
</thead>
<tbody>
<tr class="odd">
<td style="text-align: center;">Always add context to errors</td>
</tr>
<tr class="even">
<td style="text-align: center;">Never ignore errors</td>
</tr>
<tr class="odd">
<td style="text-align: center;">Use fatal errors carefully</td>
</tr>
<tr class="even">
<td style="text-align: center;">Create fault-tolerant programs</td>
</tr>
</tbody>
</table>
<div id="add-context-to-errors" class="anchor"></div>
<h2 data-number="9.1"><span class="header-section-number">9.1</span> Add context to errors <a href="#add-context-to-errors"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<p>When creating errors, you should give enough information to users (but also to the team that will operate maintenance on your application). Errors without context are harder to understand, and finding their origin in sources can be challenging.</p>
<div id="do-not-ignore-errors" class="anchor"></div>
<h2 data-number="9.2"><span class="header-section-number">9.2</span> Do not ignore errors <a href="#do-not-ignore-errors"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<p>It’s maybe obvious, but still, many developers are making this mistake. Errors that arise should be handled :</p>
<ul>
<li><p>returned to the caller</p>
<ul>
<li>or treated (your code implement some kind of auto-correction mechanism)</li>
</ul></li>
</ul>
<div id="use-fatal-carefully" class="anchor"></div>
<h2 data-number="9.3"><span class="header-section-number">9.3</span> Use fatal carefully <a href="#use-fatal-carefully"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<p>When you make a call to <span v-highlightjs><code class="go" v-pre style="display: inline">log.Fatal</code></span> or <span v-highlightjs><code class="go" v-pre style="display: inline">log.Fatalf</code></span> you are implicitly forcing your program to exit abruptly (with an <span v-highlightjs><code class="go" v-pre style="display: inline">os.Exit(1)</code></span>).“The program terminates immediately; deferred functions are not run.” (os/proc.go). Deferred functions are often used for cleaning logic (for instance closing file descriptors). Consequently, not running them might prevent the program from executing cleanup logic.</p>
<div id="create-fault-tolerant-programs" class="anchor"></div>
<h2 data-number="9.4"><span class="header-section-number">9.4</span> Create fault-tolerant programs <a href="#create-fault-tolerant-programs"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<p>The term “fault-tolerance” is used a lot by hardware engineers. Most hardware components are designed to handle failures graciously and to recover from them. Software engineers should also build their programs to tolerate errors. A program should be able to serve its purpose despite failures (which can be <strong>transient</strong> or <strong>permanent</strong>).</p>
<p>What does it mean for go programmers, what techniques could we employ to improve our programs?</p>
<div id="recover-from-errors" class="anchor"></div>
<h4 data-number="9.4.0.1"><span class="header-section-number">9.4.0.1</span> Recover from errors <a href="#recover-from-errors"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h4>
<p>If an error occurs, examine it to determine if it’s <strong>recoverable</strong> or not.</p>
<p>For instance, you are building a program that makes a call to a web service. During the execution of your program, the call failed. The source of failure is the network (your server has been disconnected from the internet). This error is recoverable, meaning that you can recover from the error because the network will become available again sometime.</p>
<p>If the call to your web service successfully pass through the network but returned an HTTP 400 error (“Bad request”) the error is not recoverable. Obviously, you had made a mistake when you developed your web service client. An human intervention will be necessary to fix this.</p>
<div id="implement-a-fallback-option." class="anchor"></div>
<h4 data-number="9.4.0.2"><span class="header-section-number">9.4.0.2</span> Implement a fallback option. <a href="#implement-a-fallback-option."><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h4>
<p>A fallback option is “is a contingency option to be taken if the preferred choice is unavailable” (Wikipedia). In our program, for instance, the network call is not possible or has returned an error. We should think about options.</p>
<p>Options will not be the same if the error is recoverable or not.</p>
<p>If you experienced a network failure, instead of directly returning the error, you could implement a <strong>retry</strong> mechanism. You will retry to contact the web service a configurable number of times.</p>
<div id="application" class="anchor"></div>
<h1 data-number="10"><span class="header-section-number">10</span> Application <a href="#application"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h1>
<div id="problem" class="anchor"></div>
<h2 data-number="10.1"><span class="header-section-number">10.1</span> Problem <a href="#problem"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<p>In an existing system, the invoices generated by a group of hotels are simple text files. Those files are stored into hard drives stored in the archive room. The new software will need to load all those invoices to operate.</p>
<p>On figure <a href="#fig:An-example-invoice-errors" data-reference-type="ref" data-reference="fig:An-example-invoice-errors">2</a> you can see an example invoice.</p>
<figure>
<b-img :src="require('@/assets/images/invoice_example.png')" alt="An example invoice[fig:An-example-invoice-errors]"  fluid thumbnail class="img-book"></b-img><figcaption aria-hidden="true">An example invoice<span id="fig:An-example-invoice-errors" label="fig:An-example-invoice-errors">[fig:An-example-invoice-errors]</span></figcaption>
</figure>
<p>Your client said that :</p>
<ul>
<li><p>Invoices are encoded using CSV.</p></li>
<li><p>Invoices have been emitted from July 2003 to July 2020</p></li>
<li><p>The amount of money is formated :</p>
<ul>
<li><p>in US dollars</p></li>
<li><p>all multiplied by 100.</p>
<ul>
<li>ex: $13.54 will be 1354 in the file</li>
</ul></li>
</ul></li>
<li><p>They are all stored in a directory.</p></li>
<li><p>In this directory, they are sub-directories.</p></li>
<li><p>Each subdirectory is dedicated to a month.</p></li>
<li><p>Each subdirectory is named following the same pattern :</p>
<ul>
<li><p>Month-Year.</p></li>
<li><p>Ex: <span v-highlightjs><code class="go" v-pre style="display: inline">October-2008</code></span>.</p></li>
</ul></li>
</ul>
<p>You are asked to build a program that will :</p>
<ul>
<li><p>Read all invoices files</p></li>
<li><p>Compute :</p>
<ul>
<li><p>The total amount of VAT that the hotel collected</p></li>
<li><p>The total amount of money paid by customers (VAT Inc.)</p></li>
<li><p>The total amount of invoices VAT Excluded</p></li>
<li><p>Those amounts have to be given for each month</p></li>
</ul></li>
</ul>
<p>The program has to be fault-tolerant. Maybe that some invoices are not that clean... To do this exercise, you can download the invoice folder. It’s located here :.</p>
<p>The file to download is a zip. On figure <a href="#fig:data-errors-folder" data-reference-type="ref" data-reference="fig:data-errors-folder">3</a> you can see a screenshot that lists all subdirectories. On figure <a href="#fig:Contents-of-one-subdir" data-reference-type="ref" data-reference="fig:Contents-of-one-subdir">4</a> you can see the contents of one subdirectory.</p>
<figure>
<b-img :src="require('@/assets/images/data-folder-application.png')" alt="Screenshot of the folder containing the invoices. Each month is located in a directory[fig:data-errors-folder]"  fluid thumbnail class="img-book"></b-img><figcaption aria-hidden="true">Screenshot of the folder containing the invoices. Each month is located in a directory<span id="fig:data-errors-folder" label="fig:data-errors-folder">[fig:data-errors-folder]</span></figcaption>
</figure>
<figure>
<b-img :src="require('@/assets/images/data_subdirectory_contents.png')" alt="Contents of the directory April-2015[fig:Contents-of-one-subdir]"  fluid thumbnail class="img-book"></b-img><figcaption aria-hidden="true">Contents of the directory April-2015<span id="fig:Contents-of-one-subdir" label="fig:Contents-of-one-subdir">[fig:Contents-of-one-subdir]</span></figcaption>
</figure>
<div id="tips-recommendations" class="anchor"></div>
<h2 data-number="10.2"><span class="header-section-number">10.2</span> Tips / Recommendations <a href="#tips-recommendations"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<ul>
<li><p>You need to find a way to compute the three figures for each month between July 2003 to 2020.</p></li>
<li><p>Maybe a for loop can help.</p></li>
<li><p>There is a pattern in the name of directories; you can use that in your code...</p></li>
<li><p>You will need to use functions from the packages :</p>
<ul>
<li><p>fmt</p></li>
<li><p>github.com/Rhymond/go-money</p></li>
<li><p>log</p></li>
<li><p>os</p></li>
<li><p>time</p></li>
<li><p>“encoding/csv”</p></li>
</ul></li>
<li><p>You are encouraged to use the external module <strong>github.com/Rhymond/go-money.</strong></p>
<ul>
<li>With this module, you can handle the money computation</li>
</ul></li>
<li><p>Before coding the real implementation, you can write pseudocode (a description written in English of each step performed by your program</p></li>
</ul>
<div id="solution" class="anchor"></div>
<h2 data-number="10.3"><span class="header-section-number">10.3</span> Solution <a href="#solution"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<div id="idea" class="anchor"></div>
<h3 data-number="10.3.1"><span class="header-section-number">10.3.1</span> Idea <a href="#idea"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h3>
<p>We need to iterate through all directories. Each directory contains data specific to a month. We can predict their names. With the directory’s name, we can then open it and read all files contained in it.</p>
<p>We need to parse its contents for each file and retrieve the vat and the total vat excluded. The grand total is simply (vat + vat Excluded). Hence it is not necessary to get it.</p>
<p>After each month, we print the totals for the month, and then we need to reinitialize the counters!</p>
<div id="pseudocode" class="anchor"></div>
<h3 data-number="10.3.2"><span class="header-section-number">10.3.2</span> Pseudocode <a href="#pseudocode"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h3>
<ul>
<li><p>For each month between <u>July 2007</u> and <u>July 2020</u></p>
<ul>
<li><p><strong>totalVat</strong> = 0</p></li>
<li><p><strong>totalVatExc</strong> = 0</p></li>
<li><p><strong>folderName</strong> = month-year</p></li>
<li><p>open <strong>folderName</strong></p></li>
<li><p>k = 0</p></li>
<li><p>For each file in directory</p>
<ul>
<li><p>k++</p></li>
<li><p><strong>fileName</strong> = invoice-k</p></li>
<li><p>Open and read fileName</p></li>
<li><p>Parse contents</p></li>
<li><p><strong>totalVat</strong> = <strong>totalVat</strong> + <strong>vat</strong> extracted from invoice</p></li>
<li><p><strong>totalVatExc</strong> = <strong>totalVatExc</strong> + <strong>vatExc</strong> extracted from invoice</p></li>
</ul></li>
<li><p>Print <strong>totalVat</strong> $ <strong>totalVatExc</strong> $ for month-year</p></li>
</ul></li>
</ul>
<p><strong>Note</strong> : I strongly suggest you to stop here and try to implement the algorithm yourself.</p>
<p>You might not be able to do it completely, and it’s OK! Just try! After, read carefully the detailed solution (which is <strong>not</strong> the only valid one).</p>
<div id="implementation-1" class="anchor"></div>
<h3 data-number="10.3.3"><span class="header-section-number">10.3.3</span> Implementation <a href="#implementation-1"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h3>
<ul>
<li>First, we create the project by creating a new directory and a go.mod file.</li>
</ul>
<!-- -->
<pre v-highlightjs><code class="go" v-pre >module maximilien-andile.com/errors/application

go 1.13</code></pre>
<p>You can adapt the module path to your needs. You can create the go.mod file by hand or with the following command :</p>
<pre v-highlightjs><code class="go" v-pre >go mod init maximilien-andile.com/errors/application</code></pre>
<ul>
<li><p>Then we create our directory structure (see figure <a href="#fig:Directory-structure-errors-app" data-reference-type="ref" data-reference="fig:Directory-structure-errors-app">5</a>).</p>
<ul>
<li><p>We create a cmd directory. It will contain all our main files. Each main file will be stored in a subdirectory.</p></li>
<li><p>For the moment, we have one application. We create one directory named “compute”.</p></li>
<li><p>Inside this directory, we create a blank file compute.go. (it will be our application file).</p></li>
</ul></li>
</ul>
<figure>
<b-img :src="require('@/assets/images/errors_application_structure.png')" alt="Directory structure[fig:Directory-structure-errors-app]"  fluid thumbnail class="img-book"></b-img><figcaption aria-hidden="true">Directory structure<span id="fig:Directory-structure-errors-app" label="fig:Directory-structure-errors-app">[fig:Directory-structure-errors-app]</span></figcaption>
</figure>
<ul>
<li>Next, we write the structure of the compute.go file :</li>
</ul>
<!-- -->
<pre v-highlightjs><code class="go" v-pre >package main


func main() {

}</code></pre>
<p>Compute.go source file belongs to package main. In this source file, we define a main function.</p>
<ul>
<li>We need to iterate over time. To do that, we will use a for loop. The for loop will need a start date and an end date. Go has a standard package for time values: time!</li>
</ul>
<!-- -->
<pre v-highlightjs><code class="go" v-pre >package main

import &quot;time&quot;

const startDate = &quot;2003-07-01&quot;
const endDate = &quot;2020-07-01&quot;



func main() {
    start, err := time.Parse(&quot;2006-01-02&quot;, startDate)
    if err != nil {
        log.Fatalf(&quot;impossible to parse start date %s&quot;, err)
    }
    end, err := time.Parse(&quot;2006-01-02&quot;, endDate)
    if err != nil {
        log.Fatalf(&quot;impossible to parse end date %s&quot;, err)
    }
}</code></pre>
<p>Here we use the function Parse from package time. Let’s take a look at its documentation on the figure <a href="#fig:time.Parse-documentation" data-reference-type="ref" data-reference="fig:time.Parse-documentation">6</a> (on https://pkg.go.dev/time)</p>
<figure>
<b-img :src="require('@/assets/images/parse_function_doc.png')" alt="time.Parse documentation[fig:time.Parse-documentation]"  fluid thumbnail class="img-book"></b-img><figcaption aria-hidden="true">time.Parse documentation<span id="fig:time.Parse-documentation" label="fig:time.Parse-documentation">[fig:time.Parse-documentation]</span></figcaption>
</figure>
<p>Parse will take two parameters, a layout and the string representing the time. Both are strings. The layout indicates to Go how the input time (second parameter) is formatted.</p>
<p>The layout is not <span v-highlightjs><code class="go" v-pre style="display: inline">YYYY-MM-DD</code></span> (some experienced programmers are more used to that kind of layout) but <span v-highlightjs><code class="go" v-pre style="display: inline">2006-01-02</code></span>. A reference in time: Mon Jan 2 15:04:05 -0700 MST 2006 is used to define a layout.</p>
<p>In our example, we parse to time strings <span v-highlightjs><code class="go" v-pre style="display: inline">"2003-07-01"</code></span> and <span v-highlightjs><code class="go" v-pre style="display: inline">"2020-07-01"</code></span> which are stored into two constants. The layout here is <span v-highlightjs><code class="go" v-pre style="display: inline">"2006-01-02"</code></span>.</p>
<p>Note that the parsing operation might fail that’s why <span v-highlightjs><code class="go" v-pre style="display: inline">time.Parse</code></span> returns an error as second parameter. When an error occurs, we call <span v-highlightjs><code class="go" v-pre style="display: inline">log.Fatalf</code></span>. It will log to the standard output and do and exit the program.</p>
<p>The function returns a value of type time.Time</p>
<ul>
<li>Then we build our for loop :</li>
</ul>
<!-- -->
<pre v-highlightjs><code class="go" v-pre >for d := start; d.Unix() &lt; end.Unix(); d = d.AddDate(0, 1, 0) {

}</code></pre>
<ul>
<li><p>This loop will iterate over each month from July 2003 to July 2020. Let’s detail how it is constructed :</p>
<ul>
<li><p><u>Init statement</u>: we create the variable d. Its value is initialized with “start”.</p></li>
<li><p><u>Condition</u> : we compare d and end. The comparison is made between UNIX epochs, the number of seconds since first January</p>
<ol start="1970" type="1">
<li>to get the unit epoch from a time.Time use the <span v-highlightjs><code class="go" v-pre style="display: inline">Unix</code></span> method.</li>
</ol>
<ul>
<li>As soon as the condition is not met, the loop is terminated (Go will not execute the post statement)</li>
</ul></li>
<li><p><u>Post statement</u> : we call <span v-highlightjs><code class="go" v-pre style="display: inline">d.AddDate(0, 1, 0)</code></span> which will add one month to d and return the new date. Thanks to the assignment, the value of d will be overridden by this new date.</p></li>
</ul></li>
<li><p>The next step is to write the body of the for loop :</p></li>
</ul>
<!-- -->
<pre v-highlightjs><code class="go" v-pre >const baseDirPath = &quot;/my/base/dir&quot;

for d := start; d.Unix() &lt; end.Unix(); d = d.AddDate(0, 1, 0) {

    monthDirPath := fmt.Sprintf(&quot;%s/%s-%d&quot;, baseDirPath, d.Month(), d.Year())

    dir, err := os.Open(monthDirPath)
    if err != nil {
        log.Fatalf(&quot;failed to open directory %s: %s&quot;, monthDirPath, err)
    }

    defer dir.Close()

    list, err := dir.Readdirnames(0)
    if err != nil {
        log.Fatalf(&quot;failed to read all files in directory %s: %s&quot;, monthDirPath, err)
    }

    // iterate over each filename in list
    for _, name := range list {

        // TODO : read the file and extract data
    }
}</code></pre>
<p>Here is step by step what the algorithm does:</p>
<ol type="1">
<li><p>Initialize a local variable named <span v-highlightjs><code class="go" v-pre style="display: inline">monthDirPath</code></span> with the path of the month directory. Note that <span v-highlightjs><code class="go" v-pre style="display: inline">baseDirectory</code></span> is a constant. It is the full path of the data directory.</p>
<ol type="1">
<li>For instance : d is equal to July 2019, then <span v-highlightjs><code class="go" v-pre style="display: inline">dirPath</code></span> is equal to <span v-highlightjs><code class="go" v-pre style="display: inline">/my/base/dir/July-2019</code></span></li>
</ol></li>
<li><p>Open the directory with <span v-highlightjs><code class="go" v-pre style="display: inline">os.Open(monthDirPath)</code></span>.</p>
<ol type="1">
<li><p>The function <span v-highlightjs><code class="go" v-pre style="display: inline">os.Open</code></span> has two results :</p>
<ol type="1">
<li><p>A pointer to an <span v-highlightjs><code class="go" v-pre style="display: inline">os.File</code></span> struct</p></li>
<li><p>An error</p></li>
</ol></li>
<li><p>We handle the error by asserting that <span v-highlightjs><code class="go" v-pre style="display: inline">err</code></span> is <span v-highlightjs><code class="go" v-pre style="display: inline">nil</code></span>. If <span v-highlightjs><code class="go" v-pre style="display: inline">err</code></span> is not <span v-highlightjs><code class="go" v-pre style="display: inline">nil</code></span> we call <span v-highlightjs><code class="go" v-pre style="display: inline">log.Fatalf</code></span></p></li>
</ol></li>
<li><p>Then we tell go to call <span v-highlightjs><code class="go" v-pre style="display: inline">dir.Close</code></span> when (a) the surrounding function (ie. main) returns or (b) the corresponding goroutine panic.</p>
<ol type="1">
<li>This is a deferred statement.</li>
</ol></li>
<li><p>Then the program will get the list of <strong>files</strong> (or <strong>directories</strong>) in the base directory (thanks to the call to <span v-highlightjs><code class="go" v-pre style="display: inline">dir.Readdirnames(0)</code></span>)</p>
<ol type="1">
<li><p>Here is the documentation of the function : (n is the input parameter of the function) “If n &lt;= 0, Readdirnames returns all the names from the directory in a single slice. In this case, if Readdirnames succeeds (reads all the way to the end of the directory), it returns the slice and a nil error. If it encounters an error before the directory end, Readdirnames returns the names read until that point and a non-nil error.”</p></li>
<li><p>By setting the input parameters to <strong>0</strong> we are assured of getting a slice of all file names in the directory. If an error occurs, we call <span v-highlightjs><code class="go" v-pre style="display: inline">log.Fatalf</code></span></p></li>
</ol></li>
<li><p>Then we will iterate over the slice of file names with a for loop <span v-highlightjs><code class="go" v-pre style="display: inline">for _, name := range list</code></span></p>
<ol type="1">
<li><p>Here we use a range statement.</p></li>
<li><p>The range statement will assign at each loop a new value to the two variables<span v-highlightjs><code class="go" v-pre style="display: inline">_</code></span> and <span v-highlightjs><code class="go" v-pre style="display: inline">name</code></span></p></li>
<li><p>The first variable is named <span v-highlightjs><code class="go" v-pre style="display: inline">_</code></span> because we are not interested in it. It is the element index in the slice.</p></li>
</ol></li>
</ol>
<p>Let’s detail what we will put inside this loop :</p>
<pre v-highlightjs><code class="go" v-pre >for _, name := range list {
    // construct dir path
    filePath := fmt.Sprintf(&quot;%s/%s&quot;, monthDirPath, name)
    // extract data from dir
    vatExc, vat, err := invoice.ReadFromFile(filePath)
    if err != nil {
        log.Fatalf(&quot;failed to parse invoice %s: %s&quot;, filePath, err)
    }
    //...
}</code></pre>
<ol type="1">
<li><p>The <span v-highlightjs><code class="go" v-pre style="display: inline">filePath</code></span> is generated thanks to <span v-highlightjs><code class="go" v-pre style="display: inline">fmt.Sprintf</code></span>.</p>
<ol type="1">
<li>ex : <span v-highlightjs><code class="go" v-pre style="display: inline">"/my/base/dir/April-2005/invoice-1"</code></span></li>
</ol></li>
<li><p>Then we call the method <span v-highlightjs><code class="go" v-pre style="display: inline">ReadFromFile</code></span> from the <span v-highlightjs><code class="go" v-pre style="display: inline">invoice</code></span> package. The handling of the invoice is complicated; we have put all the logic inside an <span v-highlightjs><code class="go" v-pre style="display: inline">invoice</code></span> package. (to make the code easier to understand)</p></li>
</ol>
<p>Let’s see the invoice package :</p>
<pre v-highlightjs><code class="go" v-pre >package invoice

//...

func readCSV(filename string) ([]string, error) {
    invoiceCSV, err := os.Open(filename)
    defer invoiceCSV.Close()
    if err != nil {
        return nil, err
    }
    reader := csv.NewReader(invoiceCSV)
    record, err := reader.Read()
    if err == io.EOF {
        return nil, errors.New(&quot;file is empty&quot;)
    }
    if err != nil {
        return nil, err
    }
    return record, nil
}</code></pre>
<p>The first function that we will dissect is <span v-highlightjs><code class="go" v-pre style="display: inline">readCSV</code></span>. This function returns a slice of strings (which represent data contained in a CSV file) and an error. Note that this function is unexported.</p>
<ol type="1">
<li><p>The file is opened with <span v-highlightjs><code class="go" v-pre style="display: inline">os.Open</code></span></p></li>
<li><p>We do not forget the deferred call to <span v-highlightjs><code class="go" v-pre style="display: inline">Close</code></span> (method defined on <span v-highlightjs><code class="go" v-pre style="display: inline">invoiceCSV</code></span>)</p></li>
<li><p>Then we will read the CSV file.</p>
<ol type="1">
<li><p>The first step is to create a CSV Reader : <span v-highlightjs><code class="go" v-pre style="display: inline">csv.NewReader(invoiceCSV)</code></span></p></li>
<li><p>Here is the signature of <span v-highlightjs><code class="go" v-pre style="display: inline">NewReader</code></span> : <span v-highlightjs><code class="go" v-pre style="display: inline">func NewReader(r io.Reader) *Reader</code></span></p></li>
<li><p>It takes an interface <span v-highlightjs><code class="go" v-pre style="display: inline">io.Reader</code></span> as input. (The first result of <span v-highlightjs><code class="go" v-pre style="display: inline">os.Open</code></span> is an <span v-highlightjs><code class="go" v-pre style="display: inline">io.Reader</code></span> (it implements the necessary methods))</p></li>
<li><p>The reader has a method <span v-highlightjs><code class="go" v-pre style="display: inline">Read</code></span> . From the <span v-highlightjs><code class="go" v-pre style="display: inline">csv</code></span> package documentation it will read the first CSV record.</p></li>
<li><p>Read returns an error that can cover two cases</p>
<ol type="1">
<li><p>When the reader has no data left to read. In this case <span v-highlightjs><code class="go" v-pre style="display: inline">err</code></span> is equal to <span v-highlightjs><code class="go" v-pre style="display: inline">io.EOF</code></span></p></li>
<li><p>When something went wrong during the read.</p></li>
</ol></li>
<li><p>In our error handling, we distinguish the two cases to add information for the function caller.</p></li>
</ol></li>
<li><p>When no error is encountered, the variable <span v-highlightjs><code class="go" v-pre style="display: inline">record</code></span> is returned along with the value <span v-highlightjs><code class="go" v-pre style="display: inline">nil</code></span>. Hence, the caller knows that everything went perfectly.</p></li>
</ol>
<p>Here is the only exported function of the <span v-highlightjs><code class="go" v-pre style="display: inline">invoice</code></span> package :</p>
<pre v-highlightjs><code class="go" v-pre >package invoice

import (
    &quot;encoding/csv&quot;
    &quot;errors&quot;
    &quot;io&quot;
    &quot;os&quot;
    &quot;strconv&quot;

    &quot;github.com/Rhymond/go-money&quot;
)

func ReadFromFile(filename string) (*money.Money, *money.Money, error) {
    record, err := readCSV(filename)
    if err != nil {
        return nil, nil, err
    }
    // record: invoice number [0326582789 Unicornquiver 126730 25346 152076 5]
    vatExcConverted, err := strconv.Atoi(record[2])
    if err != nil {
        return nil, nil, err
    }
    vatExc := money.New(int64(vatExcConverted), &quot;USD&quot;)
    vatConverted, err := strconv.Atoi(record[3])
    if err != nil {
        return nil, nil, err
    }
    vat := money.New(int64(vatConverted), &quot;USD&quot;)
    return vatExc, vat, nil
}</code></pre>
<p>Take a look at the import statement. We use the money module. The path of this module is <span v-highlightjs><code class="go" v-pre style="display: inline">github.com/Rhymond/go-money</code></span>. The code is available freely on Github https://github.com/Rhymond/go-money. The author (Rhymond) published it under the MIT license<a href="#fn1" class="footnote-ref" id="fnref1" role="doc-noteref"><sup>1</sup></a>.</p>
<p>This module will allow you to manipulate money amounts.</p>
<p>To use it in our project, we need to tell Go to get a copy of the code locally :</p>
<pre v-highlightjs><code class="go" v-pre >$ go get github.com/Rhymond/go-money</code></pre>
<p>It will :</p>
<ol type="1">
<li><p>download the module code</p></li>
<li><p>modify the <span v-highlightjs><code class="go" v-pre style="display: inline">go.mod</code></span> (and <span v-highlightjs><code class="go" v-pre style="display: inline">go.sum</code></span>) file to add the dependency :</p></li>
</ol>
<!-- -->
<pre v-highlightjs><code class="go" v-pre >module maximilien-andile.com/errors/application

go 1.13

require (
    github.com/Rhymond/go-money v1.0.1
)</code></pre>
<ul>
<li><p>The function takes a single parameter: a filename.</p></li>
<li><p>It returns 3 results : <span v-highlightjs><code class="go" v-pre style="display: inline">*money.Money, *money.Money, error</code></span></p></li>
</ul>
<p>Let’s zoom now on the exported <span v-highlightjs><code class="go" v-pre style="display: inline">ReadFromFile</code></span> function. The objective of this function is to parse an invoice to return two values : (1) the VAT excluded amount and the (2) vat amount.</p>
<ul>
<li><p>The first action here is to call the <span v-highlightjs><code class="go" v-pre style="display: inline">readCSV</code></span> function : <span v-highlightjs><code class="go" v-pre style="display: inline">record, err := readCSV(filename)</code></span>. Note that, as always, the error value (err) is controlled.</p></li>
<li><p><strong>(A)</strong> Let’s explain the instruction : <span v-highlightjs><code class="go" v-pre style="display: inline">vatExcConverted, err := strconv.Atoi(record[2])</code></span></p>
<p><span v-highlightjs><code class="go" v-pre style="display: inline">record[2]</code></span> will extract from the record slice the value at index 2 (third position, remember that indexes of slices begin at 0)</p>
<p><span v-highlightjs><code class="go" v-pre style="display: inline">record[2]</code></span> is of type string (remember that <span v-highlightjs><code class="go" v-pre style="display: inline">record</code></span> is of type <span v-highlightjs><code class="go" v-pre style="display: inline">[]string</code></span>)</p>
<ul>
<li>We want to convert it to an <strong>integer</strong> (to use the money library). To do that, we use <span v-highlightjs><code class="go" v-pre style="display: inline">strconv.Atoi</code></span></li>
</ul>
<p><span v-highlightjs><code class="go" v-pre style="display: inline">vatExcConverted</code></span> will be of type <span v-highlightjs><code class="go" v-pre style="display: inline">int</code></span>.</p></li>
<li><p><strong>(B)</strong> Then we create a pointer to a new <span v-highlightjs><code class="go" v-pre style="display: inline">money.Money</code></span> struct. To do so, we use the statement : <span v-highlightjs><code class="go" v-pre style="display: inline">vatExc := money.New(int64(vatExcConverted), "USD")</code></span>. It will create an amount of money (in dollar) from the <span v-highlightjs><code class="go" v-pre style="display: inline">vatExcConverted</code></span> value.</p></li>
<li><p><strong>Important</strong> : <span v-highlightjs><code class="go" v-pre style="display: inline">money.Newt</code></span> expects an int64; that’s why we convert it. Another important point amounts of money are treated it as integer, meanings they are multiplied by 100. $15.26 has to be multiplied by 100 (=1526) to be injected into <span v-highlightjs><code class="go" v-pre style="display: inline">money.New</code></span>. We do not need to do the conversion because the amounts are already multiplied by 100 in our dataset.</p></li>
<li><p>We repeat <strong>(A)</strong> and <strong>(B)</strong> to get the vat amount.</p></li>
<li><p>The two amounts are returned. Note that we return <strong>pointers</strong>.</p></li>
</ul>
<p>We are done with the invoice package. Here is the complete code :</p>
<pre v-highlightjs><code class="go" v-pre >// errors/application/solution/invoice/invoice.go
package invoice

import (
    &quot;encoding/csv&quot;
    &quot;errors&quot;
    &quot;io&quot;
    &quot;os&quot;
    &quot;strconv&quot;

    &quot;github.com/Rhymond/go-money&quot;
)

// ReadFromFile will open the file specified in parameter
// it will parse the csv data contained inside
// Example file content : 2360520710,Winghickory,72224,14445,86669,8
// The column 2 and 3 will be interpreted as dollar amount multiplied by 100
func ReadFromFile(filename string) (*money.Money, *money.Money, error) {
    record, err := readCSV(filename)
    if err != nil {
        return nil, nil, err
    }
    // record: invoice number [0326582789 Unicornquiver 126730 25346 152076 5]
    vatExcConverted, err := strconv.Atoi(record[2])
    if err != nil {
        return nil, nil, err
    }
    vatExc := money.New(int64(vatExcConverted), &quot;USD&quot;)
    vatConverted, err := strconv.Atoi(record[3])
    if err != nil {
        return nil, nil, err
    }
    vat := money.New(int64(vatConverted), &quot;USD&quot;)
    return vatExc, vat, nil
}

func readCSV(filename string) ([]string, error) {
    invoiceCSV, err := os.Open(filename)
    defer invoiceCSV.Close()
    if err != nil {
        return nil, err
    }
    reader := csv.NewReader(invoiceCSV)
    record, err := reader.Read()
    if err == io.EOF {
        return nil, errors.New(&quot;file is empty&quot;)
    }
    if err != nil {
        return nil, err
    }
    return record, nil
}</code></pre>
<p>And here is the complete code for the main package :</p>
<pre v-highlightjs><code class="go" v-pre >// errors/application/solution/cmd/compute/compute.go
package main

import (
    &quot;fmt&quot;
    &quot;log&quot;
    &quot;os&quot;
    &quot;time&quot;

    &quot;github.com/Rhymond/go-money&quot;
    &quot;maximilien-andile.com/errors/application/invoice&quot;
)

const startDate = &quot;2003-07-01&quot;
const endDate = &quot;2020-07-01&quot;
const baseDirPath = &quot;/Users/maximilienandile/Documents/DEV/goBook/errors/application/data&quot;

var totalVat *money.Money
var totalVatExc *money.Money

func init() {
    totalVat = money.New(0, &quot;USD&quot;)
    totalVatExc = money.New(0, &quot;USD&quot;)
}

func main() {
    start, err := time.Parse(&quot;2006-01-02&quot;, startDate)
    if err != nil {
        log.Fatalf(&quot;impossible to parse start date %s&quot;, err)
    }
    end, err := time.Parse(&quot;2006-01-02&quot;, endDate)
    if err != nil {
        log.Fatalf(&quot;impossible to parse end date %s&quot;, err)
    }
    // from start to end date add 1 month at each iteration
    for d := start; d.Unix() &lt; end.Unix(); d = d.AddDate(0, 1, 0) {
        // create a var that will contain the name of the dir to open
        monthDirPath := fmt.Sprintf(&quot;%s/%s-%d&quot;, baseDirPath, d.Month(), d.Year())
        // open the directory
        dir, err := os.Open(monthDirPath)
        if err != nil {
            log.Fatalf(&quot;failed to open directory %s: %s&quot;, monthDirPath, err)
        }
        // defer the closing of the dir
        defer dir.Close()
        // read all files and folder into the dir
        list, err := dir.Readdirnames(0)
        if err != nil {
            log.Fatalf(&quot;failed to read all files in directory %s: %s&quot;, monthDirPath, err)
        }
        // iterate for each filename in list
        for _, name := range list {
            // construct dir path
            filePath := fmt.Sprintf(&quot;%s/%s&quot;, monthDirPath, name)
            // extract data from dir
            vatExc, vat, err := invoice.ReadFromFile(filePath)
            if err != nil {
                log.Fatalf(&quot;failed to parse invoice %s: %s&quot;, filePath, err)
            }
            totalVat, err = totalVat.Add(vat)
            if err != nil {
                log.Fatalf(&quot;impossible to add VAT to counter&quot;)
            }
            totalVatExc, err = totalVatExc.Add(vatExc)
            if err != nil {
                log.Fatalf(&quot;impossible to add VAT to counter&quot;)
            }
        }
    }

    log.Println(&quot;total VAT&quot;, totalVat.Display())
    log.Println(&quot;total VAT Exc&quot;, totalVatExc.Display())

}</code></pre>
<p>We did not cover the following statements :</p>
<pre v-highlightjs><code class="go" v-pre >vatExc, vat, err := invoice.ReadFromFile(filePath)
if err != nil {
    log.Fatalf(&quot;failed to parse invoice %s: %s&quot;, filePath, err)
}
totalVat, err = totalVat.Add(vat)
if err != nil {
    log.Fatalf(&quot;impossible to add VAT to counter&quot;)
}
totalVatExc, err = totalVatExc.Add(vatExc)
if err != nil {
    log.Fatalf(&quot;impossible to add VAT to counter&quot;)
}</code></pre>
<p>We extract the two amounts from the invoice file, and then we add them to our global amount (<span v-highlightjs><code class="go" v-pre style="display: inline">totalVat</code></span> and <span v-highlightjs><code class="go" v-pre style="display: inline">totalVatExc</code></span>). To make additions, we use the convenient method <span v-highlightjs><code class="go" v-pre style="display: inline">Add</code></span>. Note that addition can fail. The error is handled by the common method.</p>
<p>The two last statements will print the total amounts :</p>
<pre v-highlightjs><code class="go" v-pre >log.Println(&quot;total VAT&quot;, totalVat.Display())
log.Println(&quot;total VAT Exc&quot;, totalVatExc.Display())</code></pre>
<div id="log.fatalf-and-deferred-functions" class="anchor"></div>
<h4 data-number="10.3.3.1"><span class="header-section-number">10.3.3.1</span> log.Fatalf and deferred functions <a href="#log.fatalf-and-deferred-functions"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h4>
<p>When log.Fatal is called the <strong>deferred functions are not run</strong>. In our case, the deferred function will close the file opened. File descriptors opened to read the files are not closed explicitly by our program. On UNIX based systems, file descriptors will be closed by the OS when <span v-highlightjs><code class="go" v-pre style="display: inline">Exit</code></span> is called (internally <span v-highlightjs><code class="go" v-pre style="display: inline">log.Fatal</code></span> calls <span v-highlightjs><code class="go" v-pre style="display: inline">os.Exit(1)</code></span>).</p>
<div id="bonus-questions" class="anchor"></div>
<h2 data-number="10.4"><span class="header-section-number">10.4</span> Bonus questions <a href="#bonus-questions"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<dl>
<dt>1</dt>
<dd><p>We forgot to print one amount. Read again the requirement to discover which amount and add the fix to the program.</p>
</dd>
<dt>2</dt>
<dd><p>In this program, as soon as an invoice is malformed, the program crashes. We might want to avoid that. How can you do that?</p>
</dd>
</dl>
<div id="bonus-questions-answers" class="anchor"></div>
<h2 data-number="10.5"><span class="header-section-number">10.5</span> Bonus questions answers <a href="#bonus-questions-answers"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<dl>
<dt>1</dt>
<dd><p>The missing amount is <u>Total VAT included</u>. We can choose to extract it from invoices (modify the invoice package). But there is a quicker way: we just need to add <span v-highlightjs><code class="go" v-pre style="display: inline">totalVat</code></span> and <span v-highlightjs><code class="go" v-pre style="display: inline">totalVatExc</code></span>. We need to create a new variable <span v-highlightjs><code class="go" v-pre style="display: inline">totalVatInc</code></span> which is of type <span v-highlightjs><code class="go" v-pre style="display: inline">*money.Money</code></span>. We initialize it in the init function.</p>
</dd>
</dl>
<!-- -->
<pre v-highlightjs><code class="go" v-pre >// errors/application/bonus1/cmd/compute/compute.go
package main

//...

var totalVat *money.Money
var totalVatExc *money.Money
var totalVatInc *money.Money

func init() {
    totalVat = money.New(0, &quot;USD&quot;)
    totalVatExc = money.New(0, &quot;USD&quot;)
    totalVatInc = money.New(0, &quot;USD&quot;)
}

func main(){
    //...
    log.Println(&quot;total VAT&quot;, totalVat.Display())
    log.Println(&quot;total VAT Exc&quot;, totalVatExc.Display())
    // Total VAT included = VAT + Total VAT Exc
    totalVatInc, err = totalVat.Add(totalVatExc)
    if err != nil {
        log.Fatalf(&quot;impossible to add total VAT + Total VAT Exc  %s&quot;, err)
    }
    log.Println(&quot;total VAT Inc&quot;, totalVatInc.Display())
}</code></pre>
<dl>
<dt>2</dt>
<dd><p>When we cannot read an invoice, the program stops. Our data source might have several bad invoices. When a bad invoice cannot be treated, we can simply log an error and continue the treatment of others. We use <span v-highlightjs><code class="go" v-pre style="display: inline">continue</code></span> to go to the next iteration in the for loop.</p>
</dd>
</dl>
<!-- -->
<pre v-highlightjs><code class="go" v-pre >// errors/application/bonus2/cmd/compute/compute.go 

// ...

vatExc, vat, err := invoice.ReadFromFile(filePath)
if err != nil {
    log.Printf(&quot;failed to parse invoice %s: %s&quot;, filePath, err)
    continue
}</code></pre>
<div id="test-yourself" class="anchor"></div>
<h1 data-number="11"><span class="header-section-number">11</span> Test yourself <a href="#test-yourself"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h1>
<div id="questions" class="anchor"></div>
<h2 data-number="11.1"><span class="header-section-number">11.1</span> Questions <a href="#questions"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<ol type="1">
<li><p><span v-highlightjs><code class="go" v-pre style="display: inline">error</code></span> is a concrete type that is part of the standard library. True or False?</p></li>
<li><p>What is the purpose of the <span v-highlightjs><code class="go" v-pre style="display: inline">Unwrap</code></span> method?</p></li>
<li><p>How to <strong>wrap</strong> an error with <span v-highlightjs><code class="go" v-pre style="display: inline">fmt.Errorf</code></span>?</p></li>
<li><p>What is a “sentinel error”?</p></li>
<li><p>How to create a new error?</p></li>
<li><p>How to check if a function has failed?</p></li>
</ol>
<div id="answers" class="anchor"></div>
<h2 data-number="11.2"><span class="header-section-number">11.2</span> Answers <a href="#answers"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h2>
<ol type="1">
<li><p><span v-highlightjs><code class="go" v-pre style="display: inline">error</code></span> is a concrete type that is part of the standard library. True or False?</p>
<ol type="1">
<li><p>False, <span v-highlightjs><code class="go" v-pre style="display: inline">error</code></span> is an interface defined in the standard library</p></li>
<li><p>It has a single method named Error that returns a <span v-highlightjs><code class="go" v-pre style="display: inline">string</code></span></p></li>
</ol>
<!-- -->
<pre v-highlightjs><code class="go" v-pre >type error interface {
    Error() string
}</code></pre></li>
<li><p>What is the purpose of the <span v-highlightjs><code class="go" v-pre style="display: inline">Unwrap</code></span> method?</p>
<ol type="1">
<li><p>The <span v-highlightjs><code class="go" v-pre style="display: inline">Unwrap</code></span> method returns an underlying error “wrapped” into another error.</p></li>
<li><p>An underlying error is an error contained in another error.</p></li>
</ol></li>
<li><p>How to <strong>wrap</strong> an error with <span v-highlightjs><code class="go" v-pre style="display: inline">fmt.Errorf</code></span>?</p>
<ol type="1">
<li><p>Use the formatting verb <span v-highlightjs><code class="go" v-pre style="display: inline">%w</code></span> (w = wrap)</p></li>
<li><p>example : <span v-highlightjs><code class="go" v-pre style="display: inline">return nil, fmt.Errorf("impossible to read data: %w",err)</code></span></p></li>
</ol></li>
<li><p>What is a “sentinel error”?</p>
<ol type="1">
<li><p>A “sentinel error” is a variable that is exported by a package</p></li>
<li><p>This variable has the type interface <span v-highlightjs><code class="go" v-pre style="display: inline">error</code></span></p></li>
<li><p>The package will return this sentinel error on specific cases that are documented</p></li>
<li><p>Callers can then trigger specific behavior when they get the sentinel error.</p></li>
<li><p>ex :</p>
<p><span v-highlightjs><code class="go" v-pre style="display: inline">var EOF = errors.New("EOF")</code></span> from <span v-highlightjs><code class="go" v-pre style="display: inline">io</code></span> package</p>
<p><span v-highlightjs><code class="go" v-pre style="display: inline">var ErrFlagTerminator = errors.New("flag terminator")</code></span> from <span v-highlightjs><code class="go" v-pre style="display: inline">cmdflag</code></span> package</p></li>
</ol></li>
<li><p>How to create a new error?</p>
<ol type="1">
<li><p>Create a type that implements the error interface, create a variable of that type and take its address.</p></li>
<li><p>Or, call <span v-highlightjs><code class="go" v-pre style="display: inline">errors.New</code></span></p></li>
<li><p>Or, call <span v-highlightjs><code class="go" v-pre style="display: inline">fmt.Errorf</code></span></p></li>
<li><p>The two last solutions are easier.</p></li>
</ol></li>
<li><p>How to check if a function has failed?</p>
<ol type="1">
<li><p>The function should return an error (generally the second result)</p></li>
<li><p>check if that error is not equal to nil</p></li>
<li><p>If it’s not equal to nil, then the function failed</p></li>
<li><p>Example</p></li>
</ol>
<!-- -->
<pre v-highlightjs><code class="go" v-pre >vatExc, vat, err := invoice.ReadFromFile(filePath)
if err != nil {
    log.Printf(&quot;failed to parse invoice %s: %s&quot;, filePath, err)
    continue
}</code></pre></li>
</ol>
<div id="key-takeaways" class="anchor"></div>
<BuyCopyInvite></BuyCopyInvite>
<h1 data-number="12"><span class="header-section-number">12</span> Key Takeaways <a href="#key-takeaways"><b-icon-link45deg font-scale="0.7" class="heading-link-icon"></b-icon-link45deg></a></h1>
<ul>
<li><p>Functions and methods that can fail should return an element of type interface<span v-highlightjs><code class="go" v-pre style="display: inline">error</code></span></p></li>
<li><p><span v-highlightjs><code class="go" v-pre style="display: inline">errors.New</code></span> and <span v-highlightjs><code class="go" v-pre style="display: inline">fmt.Errorf</code></span> are two handy functions to create errors.</p></li>
<li><p>When the function called returns an error, the caller has to <strong>check if an error has occurred</strong></p>
<ul>
<li>When the error result different of <span v-highlightjs><code class="go" v-pre style="display: inline">nil</code></span>, an error has occurred, and the other results can be discarded.</li>
</ul></li>
</ul>
<!-- -->
<pre v-highlightjs><code class="go" v-pre >vatExc, vat, err := invoice.ReadFromFile(filePath)
if err != nil {
    log.Printf(&quot;failed to parse invoice %s: %w&quot;, filePath, err)
    continue
}</code></pre>
<ul>
<li><p>An error might contain one (or several) underlying error(s)</p>
<ul>
<li>We say that an error <strong>“wrap”</strong> another error.</li>
</ul></li>
<li><p>To wrap an error, use the formatting verb <span v-highlightjs><code class="go" v-pre style="display: inline">%w</code></span> with <span v-highlightjs><code class="go" v-pre style="display: inline">fmt.Errorf</code></span></p></li>
<li><p>When you return a sentinel error, you allow the caller of your method to implement a different logic when such an error has occurred</p></li>
<li><p>An error received at some point in a program might contain several wrapped <strong>errors</strong>.</p></li>
<li><p><span v-highlightjs><code class="go" v-pre style="display: inline">errors.Is</code></span> is a useful function to test if a specific error is present in the error chain</p></li>
</ul>
<section class="footnotes" role="doc-endnotes">
<hr />
<ol>
<li id="fn1" role="doc-endnote"><p>Licenses come in different flavors: MIT, Apache 2.0, ISC, BSD, GPLv2, GPLv3, AGPLv3, ...etc. It’s a complex subject. You can visit this website to know more about licenses: https://opensource.guide/legal/ which presents the different licenses.<a href="#fnref1" class="footnote-back" role="doc-backlink">↩︎</a></p></li>
</ol>
</section>

                    <!-- END CONTENT -->
                    <!-- Bibliography -->
                    <h1>Bibliography</h1>
                    <ChapterBibliography chapter-id="Chap23Errors"></ChapterBibliography>
					<!-- Next / Previous -->
					<b-row class="ml-1 mr-1 ">
						
							<b-col class="text-center border mr-1 p-2" >
								<router-link :to="{name:'Chap22Maps'}">
									<p><u><small>Previous</small></u></p>
									<p><small>Maps</small></p>
								</router-link>
							</b-col>
						
						
							<b-col class="text-center border p-1 ">
								<router-link :to="{name:'Chap24AnonymousFunctionsAndClosures'}">
									<p><u><small>Next</small></u></p>
									<p><small>Anonymous functions & closures</small></p>
								</router-link>
							</b-col>
						
					</b-row>
					<b-row class="mt-1 ml-1 mr-1">
						<b-col class="text-center border p-1 ">
							<b-link :to="{name:'Home'}" >Table of contents</b-link>
						</b-col>
					</b-row>
          			<FeedbackInvite></FeedbackInvite>
					<NewsletterInput></NewsletterInput>
					<Footer></Footer>
                </b-col>
				<b-col ></b-col>
            </b-row>
        </div>
    </div>
</template>

<script>
import TOCChapter from "@/components/toc/TocChapter";
import ChapterBibliography from "@/components/ChapterBibliography";
import NavBar from "@/components/NavBar";
import { BIconLink45deg } from 'bootstrap-vue'
import Footer from "@/components/Footer";
import ChapterHeading from "@/components/ChapterHeading";
import BuyCopyInvite from "@/components/BuyCopyInvite";
import NewsletterInput from "@/components/NewsletterInput";
import FeedbackInvite from "@/components/FeedbackInvite";

const title = "Errors - Practical Go Lessons"
const description = "How to create errors in Go. How to use the error interface, how to use sentinel errors and how to handle errors"

export default {
  name: "Chap23Errors",
  components: {FeedbackInvite,BuyCopyInvite,NewsletterInput,ChapterHeading, ChapterBibliography,TOCChapter,NavBar,BIconLink45deg, Footer},

  created() {
    window.scrollTo(0,0);
  },data () {return {publicPath: process.env.BASE_URL}},
  metaInfo: {
      title: title,
      htmlAttrs: {
        lang: 'en',
      },
      meta: [
        { charset: 'utf-8' },
        { name: 'description', content: description },
        { name: 'robots', content: "index, follow" },
        { property: 'og:locale', content: process.env.VUE_APP_SITE_LOCALE_META },
        { property: 'og:type', content: "website" },
        { property: 'og:title', content: title},
        { property: 'og:description', content: description },
        { property: 'og:url', content: window.location.href },
        { property: 'og:site_name', content: 'Practical Go Lessons' },
        { property: 'twitter:card', content: "summary_large_image" },
        { property: 'twitter:creator', content: process.env.VUE_APP_TWITTER_USERNAME }
      ],
      link : [
        { rel : "canonical", href : window.location.href}
      ]

    }
}
</script>

<style scoped>

</style>
